import React, { cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../lib/cn';

/**
 * Tooltip, the ink annotation layer.
 *
 * CSS: `desktop/system.css` `.dsk-tooltip`, `.dsk-tooltip__value`,
 * `.dsk-tooltip__meta`, `.is-visible`.
 * Behaviour: ports BEHAVIOUR #3 from
 * `behaviours/desktop-behaviours.reference.js` (`[data-tooltip]` /
 * `[data-tooltip-meta]`).
 *
 * What #3 gets right and is preserved here:
 *   - HOVER AND FOCUS both show it, so a keyboard user gets the same
 *     information a pointer user gets.
 *   - The tip is `position: fixed` against the trigger's bounding rect, so no
 *     `overflow: hidden` ancestor can clip it.
 *   - It is read-only explanation. Never put an action in a tooltip.
 *
 * React differences, deliberate:
 *   - The tip is rendered through a portal instead of `innerHTML`, so the value
 *     and meta are React children and can never be an HTML-injection route.
 *   - `aria-describedby` wires the trigger to the tip. #3 could not do this
 *     because it shared one tip element across every target.
 *   - A tooltip on a non-focusable element (the histogram bars, which are
 *     presentational and summarised by their container's `aria-label`) shows on
 *     hover only. That is correct, not a gap: the information is already in the
 *     accessible name of the thing they belong to.
 *
 * Usage: wraps exactly one element child and clones handlers onto it.
 *
 *   <Tooltip value="438 cards" meta="R4,000 to R6,000">
 *     <span className="dsk-histogram__bar" />
 *   </Tooltip>
 *
 * `placement` is `top` by default and `bottom` for the nav icon cluster, where
 * a tip above the trigger would sit over the header it belongs to. Either way
 * the tip clears its trigger completely — it never covers what it explains.
 */
export function Tooltip({ value, meta, children, disabled = false, placement = 'top' }) {
  const id = useId();
  const triggerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const below = placement === 'bottom';

  const show = useCallback(() => {
    const node = triggerRef.current;
    if (!node || disabled) return;
    const rect = node.getBoundingClientRect();
    /* Horizontal centre of the trigger, and the trigger's near edge on the
       cross axis. #3 anchored on `rect.top` for both, which is why the tip
       covered its own trigger: the box grows DOWN from that point, so an icon
       button disappeared under its own label the moment you hovered it. The
       CSS transform now lifts the whole box clear above, or drops it below. */
    setPosition({
      left: rect.left + rect.width / 2,
      top: below ? rect.bottom : rect.top,
    });
  }, [disabled, below]);

  const hide = useCallback(() => setPosition(null), []);

  /* A trigger that scrolls away must not leave its tip stranded mid-page. */
  useEffect(() => {
    if (!position) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [position, hide]);

  if (!React.isValidElement(children)) return children || null;

  const child = cloneElement(children, {
    ref: (node) => {
      triggerRef.current = node;
      const { ref } = children;
      if (typeof ref === 'function') ref(node);
      else if (ref && typeof ref === 'object') ref.current = node;
    },
    'aria-describedby': position ? id : children.props['aria-describedby'],
    onMouseEnter: (event) => {
      show();
      if (children.props.onMouseEnter) children.props.onMouseEnter(event);
    },
    onMouseLeave: (event) => {
      hide();
      if (children.props.onMouseLeave) children.props.onMouseLeave(event);
    },
    onFocus: (event) => {
      show();
      if (children.props.onFocus) children.props.onFocus(event);
    },
    onBlur: (event) => {
      hide();
      if (children.props.onBlur) children.props.onBlur(event);
    },
  });

  return (
    <>
      {child}
      {position && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={id}
              role="tooltip"
              className={cn('dsk-tooltip', below && 'dsk-tooltip--below', 'is-visible')}
              /* Geometry only. Colour, radius, shadow and the reveal transform
                 all come from `.dsk-tooltip`. */
              style={{ left: `${position.left}px`, top: `${position.top}px` }}
            >
              <span className="dsk-tooltip__value">{value}</span>
              {meta ? <span className="dsk-tooltip__meta">{meta}</span> : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default Tooltip;
